/**
 * Offline Mode Store - Handles offline data sync for POS
 * Stores pending orders locally when internet is down
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../api/axios';

const useOfflineStore = create(
  persist(
    (set, get) => ({
      // Network status
      isOnline: navigator.onLine,
      lastOnlineTime: Date.now(),
      
      // Pending items queue (stored locally when offline)
      pendingOrders: [],
      pendingSynced: false,
      
      // Cached data for offline use
      cachedMenuItems: [],
      cachedCategories: [],
      cachedSettings: null,
      lastCacheTime: null,
      
      // Sync status
      isSyncing: false,
      syncError: null,
      lastSyncTime: null,

      // Update online status
      setOnlineStatus: (status) => {
        set({ 
          isOnline: status,
          lastOnlineTime: status ? Date.now() : get().lastOnlineTime
        });
        
        // Auto-sync when coming back online
        if (status && get().pendingOrders.length > 0) {
          get().syncPendingOrders();
        }
      },

      // Add order to pending queue (when offline)
      addPendingOrder: (order) => {
        const pendingOrder = {
          ...order,
          id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          createdOffline: true,
          offlineTimestamp: new Date().toISOString(),
        };
        
        set((state) => ({
          pendingOrders: [...state.pendingOrders, pendingOrder],
          pendingSynced: false
        }));
        
        return pendingOrder;
      },

      // Remove order from pending queue
      removePendingOrder: (offlineId) => {
        set((state) => ({
          pendingOrders: state.pendingOrders.filter(o => o.id !== offlineId)
        }));
      },

      // Sync all pending orders to server using bulk endpoint
      syncPendingOrders: async () => {
        const { pendingOrders, isOnline } = get();
        
        if (!isOnline || pendingOrders.length === 0) {
          return { success: true, synced: 0, failed: 0 };
        }
        
        set({ isSyncing: true, syncError: null });
        
        try {
          // Use bulk sync endpoint
          const response = await api.post('/orders/sync_offline/', {
            orders: pendingOrders
          });
          
          const results = response.data;
          
          // Remove successfully synced orders
          if (results.synced && results.synced.length > 0) {
            for (const syncedOrder of results.synced) {
              get().removePendingOrder(syncedOrder.offline_id);
            }
          }
          
          set({ 
            isSyncing: false,
            lastSyncTime: new Date().toISOString(),
            pendingSynced: results.failed_count === 0,
            syncError: results.failed_count > 0 
              ? `Failed to sync ${results.failed_count} orders` 
              : null
          });
          
          return { 
            success: results.failed_count === 0, 
            synced: results.success_count, 
            failed: results.failed_count,
            details: results
          };
        } catch (error) {
          console.error('Offline sync error:', error);
          set({ 
            isSyncing: false,
            syncError: error.message || 'Failed to sync offline orders'
          });
          
          return { 
            success: false, 
            synced: 0, 
            failed: pendingOrders.length,
            error: error.message 
          };
        }
      },

      // Cache menu items for offline use
      cacheMenuItems: (items) => {
        set({
          cachedMenuItems: items,
          lastCacheTime: new Date().toISOString()
        });
      },

      // Cache categories for offline use
      cacheCategories: (categories) => {
        set({ cachedCategories: categories });
      },

      // Cache settings for offline use
      cacheSettings: (settings) => {
        set({ cachedSettings: settings });
      },

      // Get menu items (from cache if offline)
      getMenuItems: () => {
        return get().cachedMenuItems;
      },

      // Get categories (from cache if offline)
      getCategories: () => {
        return get().cachedCategories;
      },

      // Clear all cached data
      clearCache: () => {
        set({
          cachedMenuItems: [],
          cachedCategories: [],
          cachedSettings: null,
          lastCacheTime: null
        });
      },

      // Clear all pending orders (use with caution)
      clearPendingOrders: () => {
        set({ pendingOrders: [], pendingSynced: true });
      },

      // Get pending orders count
      getPendingCount: () => {
        return get().pendingOrders.length;
      },

      // Check if we can process orders (online or have cache)
      canProcessOrders: () => {
        const { isOnline, cachedMenuItems } = get();
        return isOnline || cachedMenuItems.length > 0;
      }
    }),
    {
      name: 'pos-offline-storage',
      partialize: (state) => ({
        pendingOrders: state.pendingOrders,
        cachedMenuItems: state.cachedMenuItems,
        cachedCategories: state.cachedCategories,
        cachedSettings: state.cachedSettings,
        lastCacheTime: state.lastCacheTime,
        lastSyncTime: state.lastSyncTime
      })
    }
  )
);

// Network status listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useOfflineStore.getState().setOnlineStatus(true);
  });
  
  window.addEventListener('offline', () => {
    useOfflineStore.getState().setOnlineStatus(false);
  });
}

export default useOfflineStore;
